
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Language } from '../App';

interface StatCardProps {
  label: string; 
  value: string; 
  suffix?: string; 
  delay: number;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, suffix = "", delay }) => {
  const [currentValue, setCurrentValue] = useState(0);
  const { ref, inView } = useInView({ threshold: 0.5, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      const numericValue = value.replace(/[^0-9]/g, '');
      const end = parseInt(numericValue);
      if (isNaN(end)) return;
      
      let start = 0;
      const duration = 2000;
      const stepTime = 20;
      const totalSteps = duration / stepTime;
      const increment = Math.ceil(end / totalSteps);
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCurrentValue(end);
          clearInterval(timer);
        } else {
          setCurrentValue(start);
        }
      }, stepTime);
      return () => clearInterval(timer);
    }
  }, [inView, value]);

  return (
    <motion.div 
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay }}
      className="bg-zinc-950/50 backdrop-blur-md border border-white/5 p-8 md:p-10 rounded-[2rem] md:rounded-[2.5rem] hover:border-orange-500/20 transition-all group relative overflow-hidden"
    >
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-orange-600/5 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
      <div className="text-3xl md:text-6xl font-black text-white mb-2 group-hover:gradient-text transition-all font-space-grotesk tracking-tighter leading-none">
        {value.startsWith('+') ? '+' : ''}
        {currentValue.toLocaleString('pt-BR')}
        {suffix}
      </div>
      <div className="text-zinc-600 font-bold uppercase tracking-[0.2em] text-[9px] md:text-[10px] group-hover:text-zinc-400 transition-colors font-space-grotesk">
        {label}
      </div>
    </motion.div>
  );
};

interface StatsProps {
  lang: Language;
}

const Stats: React.FC<StatsProps> = ({ lang }) => {
  const t = {
    pt: {
      title: "Métricas de",
      highlight: "Impacto Real",
      labels: ["Wallets Ativas", "Swaps Internos", "Depósitos via PIX", "Expansão LATAM"]
    },
    en: {
      title: "Real Impact",
      highlight: "Metrics",
      labels: ["Active Wallets", "Internal Swaps", "Fiat Deposits", "LATAM Expansion"]
    }
  }[lang];

  return (
    <section className="py-16 md:py-28 relative overflow-hidden bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-12 md:mb-20">
          <h2 className="text-3xl md:text-6xl font-black uppercase tracking-tighter italic">
            {t.title} <span className="gradient-text">{t.highlight}</span>
          </h2>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <StatCard label={t.labels[0]} value="+1900" delay={0.1} />
          <StatCard label={t.labels[1]} value="+600" delay={0.2} />
          <StatCard label={t.labels[2]} value="+5000" delay={0.3} />
          <StatCard label={t.labels[3]} value="200" suffix="%" delay={0.4} />
        </div>
      </div>
    </section>
  );
};

export default Stats;
